import React, {Component} from 'react'
import PropTypes from 'prop-types'
import Cookies from 'js-cookie'
import events from 'events-bus'
import {ThemeProvider} from 'styled-components'

import TopBar from './components/TopBar'
import MiddleBar from './components/MiddleBar'
import LowerBar from './components/LowerBar'
import BurgerNavigation from './components/BurgerNavigation'
import GlobalStyle from './theme/globalStyles'
import AppContext from './context'
import {theme} from './theme/config'
import * as SC from './App.styled'
import {DEVICE_TYPES, deviceMatching} from './utils'
import {RemoteComponent} from './components/Injected'
import {CART_SERVICE_ID} from './utils/constants'

export const GET_CART_EVENT = 'GET-CART-SUCCESS-v1'
export const ADD_TO_CART_EVENT = 'ADD-TO-CART-SUCCESS-v1'
export const AUTH_SUCCESS_EVENT = 'AUTH-SUCCESS-EVENT-v1'
export const AUTH_FAILURE_EVENT = 'AUTH-FAILURE-EVENT-v1'
export const AUTH_LOGOUT_EVENT = 'AUTH-LOGOUT-EVENT-v1'

const themeName = theme.name;
export const THEME = require(`./theme/${themeName}.js`).default

class App extends Component {
  constructor(props) {
    super(props)

    this.state = {
      device: DEVICE_TYPES.DESKTOP,
      clientWidth: 0,
      isBurgerNavOpen: false,
      isSearchFlyoutOpen: false,
      cart: null,
      exorbyteProxyUrl: '',
      isLoggedIn: false
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowSizeChange)
    this.handleWindowSizeChange()

    events.on(GET_CART_EVENT, (eventName, data) => {
      const {response: cart} = data
      this.setState({cart})
    })

    events.on(ADD_TO_CART_EVENT, (eventName, data) => {
      const {response: cart} = data
      this.setState({cart})
    })

    events.on(AUTH_SUCCESS_EVENT, () => { this.setState({ isLoggedIn: true }) })
    events.on(AUTH_LOGOUT_EVENT, () => { this.setState({ isLoggedIn: false }) })
    events.on(AUTH_FAILURE_EVENT, () => { this.setState({ isLoggedIn: false }) })

    // Service discovery JSONP request to retrieve ecs servers from Exorbyte
    window.jsonp_ecs_servers = (config) => {
      const {exoAltUrls} = config
      if (typeof exoAltUrls === 'object' && exoAltUrls.length) {
        // Balance from available servers
        const server = exoAltUrls[Math.floor(Math.random() * exoAltUrls.length)];
        this.setState({exorbyteProxyUrl: server})
      }
    }

    // Fetch service discovery JSONP script programatically after define jsonp_esc_servers callback
    const {exorbytePathKey} = this.props
    const body = document.querySelector('body')
    const exorbyteEcsServersScript = document.createElement('script')
    exorbyteEcsServersScript.src = `https://d25a50wq0hgskv.cloudfront.net/${exorbytePathKey}/sncustom/ecsServers.js.gz`
    body.appendChild(exorbyteEcsServersScript)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowSizeChange)
  }

  handleWindowSizeChange = () => {
    const {innerWidth} = window
    const {clientWidth} = document.body
    const {device: currentDevice} = this.state

    this.setState({clientWidth})
    // Device has to be inferred from window.innerWidth because CSS media queries
    // are also based in the window.innerWidth property
    // using body.clientWidth instead of window.innerWidth results in conflicts and
    // inconsistencies when a scrollbar is present in the browser, as it is included
    // in innerWidth calculation but not in clientWidth
    const targetDevice = deviceMatching(innerWidth)
    if (currentDevice !== targetDevice) {
      this.setState({device: targetDevice})
    }
  }

  toggleBurgerNav = () => {
    const {isBurgerNavOpen} = this.state
    this.setState({isBurgerNavOpen: !isBurgerNavOpen})
  }

  setBurgerNavOpen = (isBurgerNavOpen) => {
    this.setState({isBurgerNavOpen})
  }

  setSearchFlyoutOpen = (isSearchFlyoutOpen, callback = () => {}) => {
    this.setState({isSearchFlyoutOpen}, callback)
  }

  render() {
    const {...props} = this.props
    const {viaWebC, cartURL} = this.props
    const {...state} = this.state
    const {isBurgerNavOpen, isSearchFlyoutOpen} = this.state

    const hasReorder = Cookies.get('cid') !== undefined
    const reorderLink = '/account/orders/reorder'

    const rootContext = {
      ...props,
      ...state,
      hasReorder,
      reorderLink,
      toggleBurgerNav: this.toggleBurgerNav,
      setBurgerNavOpen: this.setBurgerNavOpen,
      setSearchFlyoutOpen: this.setSearchFlyoutOpen
    }

    return (
      <AppContext.Provider value={rootContext}>
        {!viaWebC && <RemoteComponent id={CART_SERVICE_ID} url={cartURL} />}
        <ThemeProvider theme={THEME}>
          <SC.Wrapper isSearchFlyoutOpen={isSearchFlyoutOpen}>
            <SC.Header className="shop-header">
              <TopBar />
              <MiddleBar />
              <LowerBar />
              {isBurgerNavOpen && <BurgerNavigation />}
              <GlobalStyle />
            </SC.Header>
          </SC.Wrapper>
        </ThemeProvider>
      </AppContext.Provider>
    )
  }
}

App.propTypes = {
  multilanguage: PropTypes.arrayOf(PropTypes.string),
  availableLanguages: PropTypes.arrayOf(PropTypes.shape({
    locale: PropTypes.string.isRequired,
    isSelected: PropTypes.bool.isRequired,
    displayLanguage: PropTypes.string.isRequired
  })),
  reorderLink: PropTypes.string.isRequired,
  helpLink: PropTypes.string.isRequired,
  translations: PropTypes.instanceOf(Map).isRequired,
  showSearch: PropTypes.bool.isRequired,
  showCart: PropTypes.bool.isRequired,
  showCategories: PropTypes.bool.isRequired,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  bonuspointsAvailable: PropTypes.bool.isRequired,
  mediaServerUrl: PropTypes.string.isRequired,
  bundle: PropTypes.string.isRequired,
  brand: PropTypes.string.isRequired,
  exorbyteId: PropTypes.string.isRequired,
  exorbytePathKey: PropTypes.string.isRequired,
  viaWebC: PropTypes.bool.isRequired,
  cartURL: PropTypes.string.isRequired
}

App.defaultProps = {
  multilanguage: [],
  availableLanguages: []
}

export default App
